import axios from 'axios';
import _debounce from 'lodash.debounce';
import _orderBy from 'lodash.orderby';
import Vue from 'vue';
import Errors from '../classes/errors';
import { COUNTRIES } from '../consts/countries';
import { isCaptchaEnabled } from '../services/captcha';
import { setCaptchaEuScript } from '../utils';
import { getFormRenderToken } from '../services/anti-bot';

Vue.component('blind-booking', {
    props: {
        initialcolorvalue: {
            value: String,
        },
        initialrangevalue: {
            value: String,
        },
        stores: {
            value: Object,
        },
        siteHandle: {
            value: String,
        },
        brandstoreResultAmount: {
            value: Number,
        },
        captchaEuPublicKey: {
            value: String,
        },
    },

    data() {
        return {
            formRenderToken: '',
            errors: new Errors(),
            rangeIndex: 0,
            colorIndex: 0,
            colorValue: null,
            rangeValue: null,
            dropdownInfo: null,
            dropdownValue: '',
            selectedBrandStore: '',
            store: '',
            zipCoords: null,
            needMoreInput: false,
            apiRequestLoading: false,
            nearbyProximityStores: null,
            countryHandle: '',
            countryValue: COUNTRIES,
        };
    },

    computed: {
        moreThenTwoStores: function () {
            if (!this.countryHandle) {
                return false;
            }
            return Object.keys(this.stores).length > 2;
        },
        hasBrandStores: function () {
            if (!this.countryHandle) {
                return false;
            }
            return Object.keys(this.stores).length > 0;
        },
        orderedStores: function () {
            return _orderBy(this.stores, 'title');
        },
    },

    watch: {
        store() {
            if (this.store.length < 4) {
                this.needMoreInput = true;
                this.nearbyProximityStores = null;
                return;
            }
            if (this.selectedBrandStore) {
                if (this.selectedBrandStore.title === this.store) {
                    return;
                }
            }
            this.selectedBrandStore = null;
            this.needMoreInput = false;
            this.searchByZipGoogleApi();
        },
    },

    mounted() {
        if (this.siteHandle !== 'default') {
            this.countryHandle = this.siteHandle;
        }

        this.colorValue = this.initialcolorvalue;
        this.rangeValue = this.initialrangevalue;

        setCaptchaEuScript();

        getFormRenderToken().then((token) => {
            this.formRenderToken = token;
        });
    },

    methods: {
        switchRangeIndex(index, rangeValue) {
            if (index != this.rangeIndex) {
                this.rangeIndex = index;
                this.rangeValue = rangeValue;
            }
        },

        switchColorIndex(index, colorValue) {
            if (index != this.colorIndex) {
                this.colorIndex = index;
                this.colorValue = colorValue;
            }
        },

        toggleDropdown(event) {
            let element = event.currentTarget;

            while ((element = element.parentElement)) {
                if (element.classList.contains('dropdown')) {
                    element.classList.toggle('dropdown--active');
                }
            }
        },

        setDropdownInfo(event) {
            this.dropdownInfo = event.currentTarget.dataset.text;
            this.toggleDropdown(event);

            const value = event.currentTarget.dataset.value;
            this.dropdownValue = value;
        },
        replaceInputValue(e) {
            this.selectedBrandStore = this.stores[e.target.dataset.value];
            this.store = this.selectedBrandStore.title;
            // Reset zipCoords to null
            this.zipCoords = null;
        },
        searchByZipGoogleApi: _debounce(function () {
            this.makeApiRequest();
        }, 300),
        makeApiRequest() {
            this.apiRequestLoading = true;
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode(
                {
                    componentRestrictions: {
                        country: this.countryValue[this.siteHandle],
                    },
                    address: encodeURI(this.store),
                },
                (results, status) => {
                    if (status === 'OK') {
                        if (results[0]) {
                            this.apiRequestLoading = false;
                            this.zipCoords = {
                                lat: results[0].geometry.location.lat(),
                                lng: results[0].geometry.location.lng(),
                            };
                            if (!this.zipCoords || this.stores.length < 1) {
                                return;
                            }
                            this.getNearbyBrandstores(
                                this.zipCoords,
                                this.stores,
                            );
                        }
                        return null;
                    } else {
                        console.error(
                            'Geocode was not successful for the following reason: ' +
                                status,
                        );
                        if (status === 'ZERO_RESULTS') {
                            this.apiRequestLoading = false;
                        }
                    }
                },
            );
        },
        getNearbyBrandstores(userLocation, brandstores) {
            const storesWithDistance = Object.values(brandstores).map(
                (store) => {
                    const distance = this.getDistance(userLocation, store);
                    return {
                        ...store,
                        distance,
                    };
                },
            );

            this.nearbyProximityStores = storesWithDistance
                .sort((a, b) => {
                    if (a.distance < b.distance) {
                        return -1;
                    }
                    if (a.distance > b.distance) {
                        return 1;
                    }
                    return 0;
                })
                .slice(0, this.brandstoreResultAmount);
        },
        getDistance(userLocation, brandstore) {
            // Bron: https://cloud.google.com/blog/products/maps-platform/how-calculate-distances-map-maps-javascript-api
            var radius = 6371.071; // Radius of the Earth in KM
            const radiusLat1 = userLocation.lat * (Math.PI / 180); // Convert degrees to radians
            const radiusLat2 = brandstore.lat * (Math.PI / 180); // Convert degrees to radians
            const differenceLat = radiusLat2 - radiusLat1; // Radian difference (latitudes)
            const differenceLng =
                (brandstore.lng - userLocation.lng) * (Math.PI / 180); // Radian difference (longitudes)
            const distance =
                2 *
                radius *
                Math.asin(
                    Math.sqrt(
                        Math.sin(differenceLat / 2) *
                            Math.sin(differenceLat / 2) +
                            Math.cos(radiusLat1) *
                                Math.cos(radiusLat2) *
                                Math.sin(differenceLng / 2) *
                                Math.sin(differenceLng / 2),
                    ),
                );
            return distance;
        },

        async onSubmit(event) {
            const form = event.currentTarget;
            if (this.submitting) {
                return;
            }

            this.submitting = true;

            try {
                if (!(await isCaptchaEnabled())) {
                    await this.handleDataSubmit(form);
                    return;
                }

                const captchaSolution = await KROT.getSolution(
                    this.captchaEuPublicKey,
                );

                await this.handleDataSubmit(form, captchaSolution);
            } catch (e) {
                throw new Error(e);
            } finally {
                this.submitting = false;
            }
        },

        async handleDataSubmit(form, captchaSolution) {
            try {
                const formData = new FormData(form);
                formData.append('formRenderToken', this.formRenderToken);
                if (captchaSolution) {
                    formData.append(
                        'captchaSolution',
                        JSON.stringify(captchaSolution),
                    );
                }
                const response = await axios.post('/', formData);

                const result = response.data;
                if (result.data.errors) {
                    this.submitting = false;
                    this.errors.clear();
                    this.errors.record(result.data.errors);
                    return;
                }
                this.errors.clear();
                window.location = this.$refs.redirectUrl.value;
            } catch (e) {
                throw new Error(e);
            }
        },
    },
});
